import React, { useCallback, useEffect, useState, Fragment } from 'react'
import DataTable from '../../../../../app/components/statelessDataTable'
import DefaultErrorMessage from '../../../../../common/components/ErrorBoundary/errorMessage'
import createColumns from '../columns/columns'
import { exportCsv } from '../../../../../common/helpers'
import notificationService from '../../../../../services/notificationsService'
import { getCompanyDisplayName, getCompanyId } from '../../../../helper'
import { formatDisplayName, formattedDate } from '../../../../../common/utils/csvUtility'
import { CSVDownloadError } from '../../../../../common/components/CSVDownloadError/CSVDownloadError'

const PaidLeaveNotifications = () => {
  const companyId = getCompanyId()
  const companyDisplayName = getCompanyDisplayName()
  const filters = { state: '' }
  const pageSize = 20

  const [loading, setLoading] = useState(Boolean(companyId))
  const [errorPopupOpen, setErrorPopupOpen] = useState(false)
  const [error, setError] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [total, setTotal] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [sortOptions, setSortOptions] = useState({
    field: 'dateOfChange',
    direction: 'asc'
  })
  const [filterOptions, setFilterOptions] = useState({
    filter: '',
    field: ''
  })
  const [columns, setColumns] = useState(createColumns({
    sortOptions,
    filterOptions,
    eventHandlers: {
      setSortOptions,
      setFilterOptions,
      setPageNumber
    }
  }))

  const fetchCurrentPaidLeaveNotifications = async () => {
    let subscribed = true
    setLoading(true)
    setNotifications([])

    try {
      const notificationsResponse = await notificationService.getCurrentPaidLeaveNotifications({
        companyId,
        searchText,
        sortOptions,
        filterOptions,
        pageNumber,
        pageSize
      })

      if (subscribed) {
        setNotifications(notificationsResponse.notifications)
        setTotal(notificationsResponse.total)
        setColumns(createColumns({
          refreshNotifications,
          sortOptions,
          filterOptions,
          eventHandlers: {
            setSortOptions,
            setFilterOptions,
            setPageNumber
          }
        }))
        setLoading(false)
      }

      return () => (subscribed = false)
    } catch (notificationFetchError) {
      setLoading(false)
      console.error({ notificationFetchError })
      setError(notificationFetchError)
    }

    return false
  }

  const refreshNotifications = () => {
    fetchCurrentPaidLeaveNotifications()
  }

  useEffect(() => {
    fetchCurrentPaidLeaveNotifications()
  }, [pageNumber, sortOptions, filterOptions, searchText, companyId])

  const handleChangePage = useCallback((event) => {
    setPageNumber(event.selected + 1)
    setNotifications([])
    setLoading(true)
  }, [])

  const handleSetSearchText = useCallback((value) => {
    setSearchText(value)
  }, [])

  const handleSetSortOptions = useCallback((value) => {
    setSortOptions(value)
  }, [])

  const handleSetPageNumber = useCallback((value) => {
    setPageNumber(value)
  }, [])

  const handleExportCsv = async (notificationsDto) => {
    try {
      const csvRows = notificationService.createNotificationsCsvRows(notificationsDto)
      const csvFilename = `${formatDisplayName(companyDisplayName)}_Paid_Leave_Notifications_${formattedDate()}.csv`
      exportCsv(csvRows, csvFilename)
    } catch (err) {
      console.error('CSV export error:', err)
      setErrorPopupOpen(true)
    }
  }

  return (
    <Fragment>
      {error ? (
        <DefaultErrorMessage error={error} data-testid="error-container" />
      ) : (
        <DataTable
          pageCount={Math.ceil(total / pageSize)}
          isLoading={loading}
          recordType="Notifications"
          showSearch
          showExport
          showLocaleFilter={false}
          eventHandlers={{
            handleChangePage,
            setSearchText: handleSetSearchText,
            setSortOptions: handleSetSortOptions,
            setPageNumber: handleSetPageNumber,
            export: () => handleExportCsv(notifications)
          }}
          columns={columns}
          data={notifications}
          filters={filters}
          sortOptions={sortOptions}
          filterOptions={filterOptions}
          pageNumber={pageNumber}
          total={total}
          searchText={searchText}
          rowsPerPage={pageSize}
        />
      )}
      <CSVDownloadError open={errorPopupOpen} onClose={() => setErrorPopupOpen(false)} />
    </Fragment>
  )
}

export default PaidLeaveNotifications
